import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FaUsers,
  FaUserPlus,
  FaDownload,
  FaChevronLeft,
  FaChevronRight,
  FaFile,
  FaSearch,
  FaFilter,
  FaTimes,
  FaEdit,
  FaExchangeAlt,
  FaUserFriends,
  FaCalendarAlt,
  FaBuilding,
  FaFolder,
  FaExclamationTriangle,
  FaUserInjured,
  FaFlask,
  FaChartBar,
  FaFlagCheckered,
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, query, orderBy, limit, startAfter, addDoc, serverTimestamp, writeBatch, where, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './ProjectsManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VisitsModal from './VisitsModal';
import WorkersModal from './WorkersModal';
import AddProjectModal from './AddProjectModal';
import EditProjectModal from './EditProjectModal';
import FileUploadModal from './FileUploadModal';
import PatientsModal from './PatientsModal';
import ConfirmationModal from './ConfirmationModal';
import ProjectStatsModal from './ProjectStatsModal';
import TableFilter from '../../components/TableFilter';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import './ProjectsManagementDarkMode.css'; // Import dark mode styles

const truncateText = (text, maxLength = 15) => {
  if (
    text === null ||
    text === undefined ||
    text === '' ||
    Number.isNaN(text) ||
    (typeof text === 'string' &&
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength
    ? `${textStr.substring(0, maxLength)}...`
    : textStr;
};

const formatTimestamp = (timestamp) => {
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp
      .toDate()
      .toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
  }
  return timestamp || '';
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object' && value.seconds) return formatTimestamp(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// isTestProject is stored as a boolean but becomes the string 'true' after
// formatValue runs, so accept both forms.
const isTestProjectFlag = (project) =>
  project?.isTestProject === true || project?.isTestProject === 'true';

const ProjectsManagementPage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const { userEmail } = useUserProfile(); // Get user email for admin check
  const isMainAdmin = userEmail === 'turanemre1999@gmail.com';
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filters, setFilters] = useState({});
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  const [showWorkersModal, setShowWorkersModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showPatientsModal, setShowPatientsModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [screenFailData, setScreenFailData] = useState(null);
  const [loadingScreenFails, setLoadingScreenFails] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [showTestProjectsOnly, setShowTestProjectsOnly] = useState(false);
  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // Fetch 20 projects per page
  const [totalProjects, setTotalProjects] = useState(0);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [pageCache, setPageCache] = useState(() => {
    // Initialize from localStorage if available
    try {
      const cached = localStorage.getItem('projectsPageCache');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error('Error loading cache:', e);
      return {};
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  // Filtering states
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [allProjectsForFilter, setAllProjectsForFilter] = useState([]);
  const [statsData, setStatsData] = useState({ total: 0, expiring: 0 });

  // Define visible columns
  const [visibleColumns, setVisibleColumns] = useState({
    customer: true,
    titleShort: true,
    protocolNumber: true,
    ethicsCommitteeDate: true,
    ethicsCommitteeNumber: true,
    ethicsCommitteePlace: true,
    firstInvoiceDate: true,
    paymentFrequency: true,
    projectCentersType: true,
    projectDate: true,
    projectDepartment: true,
    projectDueDate: true,
    projectPhase: true,
    projectStatus: true,
    coordinatorAcademician: true,
    sbFirstApprovalDate: false,
    sbFirstApprovalNumber: false,
    sbProjectNumber: false,
    titleLong: false,
    isCurrencyFixed: false,
    fixedCurrencyRate: false,
  });

  // Column definitions
  const columns = {
    customer: { label: 'Müşteri', type: 'text' },
    titleShort: { label: 'Başlık (Kısa)', type: 'text' },
    projectPhase: {
      label: 'Proje Aşaması',
      type: 'select',
      options: [
        { value: '', label: 'Tümü' },
        { value: 'Başlangıç', label: 'Başlangıç' },
        { value: 'Planlama', label: 'Planlama' },
        { value: 'Yürütme', label: 'Yürütme' },
        { value: 'İzleme ve Kontrol', label: 'İzleme ve Kontrol' },
        { value: 'Kapanış', label: 'Kapanış' },
        { value: 'Tamamlandı', label: 'Tamamlandı' },
        { value: 'Faz I', label: 'Faz I' },
        { value: 'Faz II', label: 'Faz II' },
        { value: 'Faz III', label: 'Faz III' },
        { value: 'Düşük Riskli Bilimsel Çalışma', label: 'Düşük Riskli Bilimsel Çalışma' },
        { value: 'Beşeri Tıbbi Ürünlerin Adaptif Tasarımlı Klinik Araştırması (Faz I/II)', label: 'Adaptif (Faz I/II)' },
        { value: 'Beşeri Tıbbi Ürünlerin Adaptif Tasarımlı Klinik Araştırması (Faz I/III)', label: 'Adaptif (Faz I/III)' },
        { value: 'Beşeri Tıbbi Ürünlerin Adaptif Tasarımlı Klinik Araştırması (Faz II/III)', label: 'Adaptif (Faz II/III)' },
        { value: 'Sağlık Beyanlı Ürün/Yöntem Çalışması', label: 'Sağlık Beyanlı Ürün/Yöntem' },
        { value: 'Beşeri Tıbbi Ürünlerin Gözlemsel Çalışması', label: 'Gözlemsel Çalışma' },
        { value: 'Kök hücreler ile yapılan klinik araştırması', label: 'Kök Hücre Araştırması' },
      ],
    },
    projectStatus: {
      label: 'Proje Durumu',
      type: 'select',
      options: [
        { value: '', label: 'Tümü' },
        { value: 'Aktif', label: 'Aktif' },
        { value: 'Pasif', label: 'Pasif' },
      ],
    },
    protocolNumber: { label: 'Protokol Numarası', type: 'text' },
    ethicsCommitteeDate: { label: 'Etik Kurul Tarihi', type: 'date' },
    ethicsCommitteeNumber: { label: 'Etik Kurul Numarası', type: 'text' },
    ethicsCommitteePlace: { label: 'Etik Kurul Yeri', type: 'text' },
    firstInvoiceDate: { label: 'İlk Fatura Tarihi', type: 'date' },
    paymentFrequency: { label: 'Ödeme Sıklığı', type: 'text' },
    projectCentersType: { label: 'Proje Merkezi Tipi', type: 'text' },
    projectDate: { label: 'Proje Tarihi', type: 'date' },
    projectDepartment: { label: 'Proje Departmanı', type: 'text' },
    projectDueDate: { label: 'Proje Bitiş Tarihi', type: 'date' },
    coordinatorAcademician: { label: 'Koordinatör Akademisyen', type: 'text' },
    sbFirstApprovalDate: { label: 'SB İlk Onay Tarihi', type: 'date' },
    sbFirstApprovalNumber: { label: 'SB İlk Onay Numarası', type: 'text' },
    sbProjectNumber: { label: 'SB Proje Numarası', type: 'text' },
    titleLong: { label: 'Başlık (Uzun)', type: 'text' },
    isCurrencyFixed: { label: 'Kur Sabitlemesi', type: 'text' },
    fixedCurrencyRate: { label: 'Sabit Kur (Kaçtan?)', type: 'text' },
  };

  // Calculate stats from already fetched data (avoids duplicate fetch)
  // NOTE: Must be defined before fetchAllProjectsForFilter which depends on it
  const calculateStatsFromProjects = useCallback((projectsData) => {
    const total = projectsData.length;
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    const expiring = projectsData.filter(proj => {
      if (proj.projectDueDate) {
        const dueDate = new Date(proj.projectDueDate);
        return dueDate <= thirtyDaysFromNow && dueDate >= today;
      }
      return false;
    }).length;
    setStatsData({ total, expiring });
  }, []);

  // Fetch all projects for filtering/searching
  const fetchAllProjectsForFilter = useCallback(async () => {
    try {
      const allProjectsQuery = query(collection(db, 'Projects'), orderBy('customer'));
      const querySnapshot = await getDocs(allProjectsQuery);
      let allProjects = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        
        // Migration: Move Aktif/Pasif from projectPhase to projectStatus
        let migratedData = { ...data };
        const phaseLower = data.projectPhase?.toLowerCase();
        
        if (phaseLower === 'aktif' || phaseLower === 'devam ediyor') {
          migratedData.projectStatus = 'Aktif';
          migratedData.projectPhase = '';
          updateDoc(doc.ref, {
            projectStatus: 'Aktif',
            projectPhase: ''
          }).catch(err => console.error('Migration error:', err));
        } else if (phaseLower === 'pasif' || phaseLower === 'tamamlandı') {
          migratedData.projectStatus = 'Pasif';
          migratedData.projectPhase = '';
          updateDoc(doc.ref, {
            projectStatus: 'Pasif',
            projectPhase: ''
          }).catch(err => console.error('Migration error:', err));
        } else if (!data.projectStatus) {
          migratedData.projectStatus = 'Aktif';
          updateDoc(doc.ref, {
            projectStatus: 'Aktif'
          }).catch(err => console.error('Migration error:', err));
        }
        
        return {
          id: doc.id,
          ...Object.entries(migratedData).reduce((acc, [key, value]) => {
            acc[key] = formatValue(value);
            return acc;
          }, {}),
        };
      });
      // Non-admins must never see or count test projects
      if (!isMainAdmin) {
        allProjects = allProjects.filter((project) => !isTestProjectFlag(project));
      }
      setAllProjectsForFilter(allProjects);
      setTotalProjects(allProjects.length);
      // Calculate stats from the same data (no duplicate fetch needed)
      calculateStatsFromProjects(allProjects);
      return allProjects;
    } catch (error) {
      console.error('Error fetching all projects:', error);
      return [];
    }
  }, [calculateStatsFromProjects, isMainAdmin]);

  // Fetch projects with pagination
  const fetchProjects = async (page = 1, useCache = true) => {
    // Check cache first
    if (useCache && pageCache[page]) {
      setProjects(pageCache[page]);
      setCurrentPage(page);
      return;
    }

    setIsLoading(true);
    try {
      let projectsQuery;

      if (page === 1) {
        // First page query
        projectsQuery = query(
          collection(db, 'Projects'),
          orderBy('customer'),
          limit(pageSize)
        );
      } else if (page > currentPage && lastVisible) {
        // Next page query
        projectsQuery = query(
          collection(db, 'Projects'),
          orderBy('customer'),
          startAfter(lastVisible),
          limit(pageSize)
        );
      } else if (page < currentPage && firstVisible) {
        // Previous page query - use cache or refetch from page 1
        if (pageCache[page]) {
          setProjects(pageCache[page]);
          setCurrentPage(page);
          setIsLoading(false);
          return;
        }
        // Fallback: refetch from beginning
        projectsQuery = query(
          collection(db, 'Projects'),
          orderBy('customer'),
          limit(pageSize * page)
        );
      } else {
        // Default to first page
        projectsQuery = query(
          collection(db, 'Projects'),
          orderBy('customer'),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(projectsQuery);

      let projectsData = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        
        // Migration: Move Aktif/Pasif from projectPhase to projectStatus
        let migratedData = { ...data };
        const phaseLower = data.projectPhase?.toLowerCase();
        
        if (phaseLower === 'aktif' || phaseLower === 'devam ediyor') {
          // Set projectStatus to Aktif and clear projectPhase
          migratedData.projectStatus = 'Aktif';
          migratedData.projectPhase = '';
          
          // Update in Firestore (async, don't wait)
          updateDoc(doc.ref, {
            projectStatus: 'Aktif',
            projectPhase: ''
          }).catch(err => console.error('Migration error:', err));
        } else if (phaseLower === 'pasif' || phaseLower === 'tamamlandı') {
          // Set projectStatus to Pasif and clear projectPhase
          migratedData.projectStatus = 'Pasif';
          migratedData.projectPhase = '';
          
          // Update in Firestore (async, don't wait)
          updateDoc(doc.ref, {
            projectStatus: 'Pasif',
            projectPhase: ''
          }).catch(err => console.error('Migration error:', err));
        } else if (!data.projectStatus) {
          // If no projectStatus exists, default to Aktif
          migratedData.projectStatus = 'Aktif';
          
          // Update in Firestore (async, don't wait)
          updateDoc(doc.ref, {
            projectStatus: 'Aktif'
          }).catch(err => console.error('Migration error:', err));
        }
        
        return {
          id: doc.id,
          ...Object.entries(migratedData).reduce((acc, [key, value]) => {
            acc[key] = formatValue(value);
            return acc;
          }, {}),
        };
      });

      // If fetching multiple pages for previous page navigation
      if (page < currentPage && page > 1) {
        // Get only the last pageSize items
        projectsData = projectsData.slice(-pageSize);
      }

      // Store first and last visible documents for pagination
      if (querySnapshot.docs.length > 0) {
        setFirstVisible(querySnapshot.docs[0]);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }

      setProjects(projectsData);
      setCurrentPage(page);

      // Cache the page in state and localStorage
      setPageCache(prev => {
        const newCache = {
          ...prev,
          [page]: projectsData
        };
        try {
          localStorage.setItem('projectsPageCache', JSON.stringify(newCache));
        } catch (e) {
          console.error('Error saving cache:', e);
        }
        return newCache;
      });

    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Projeler yüklenirken hata oluştu!');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear cache when data changes
  const refreshProjects = () => {
    setPageCache({});
    try {
      localStorage.removeItem('projectsPageCache');
    } catch (e) {
      console.error('Error clearing cache:', e);
    }
    setCurrentPage(1);
    // Refresh both all projects (for filtering) and paginated projects
    fetchAllProjectsForFilter().then(() => {
      fetchProjects(1, false);
    });
  };

  useEffect(() => {
    // Fetch first page and all projects for stats (single fetch for stats instead of duplicate)
    fetchProjects(1, false);
    // Fetch all projects once on mount to calculate stats and enable filtering
    fetchAllProjectsForFilter();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [fetchAllProjectsForFilter]);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    // Check if any filters or search are active (with minimum 3 characters for search)
    const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
    const hasActiveFilters = Object.keys(filters).some(key => {
      // For text filters, require at least 3 characters
      if (filters[key] && typeof filters[key] === 'string' && key !== 'projectPhase') {
        return filters[key].trim().length >= 3;
      }
      return filters[key] && filters[key] !== '';
    });

    const isAnyFilterActive = hasActiveSearch || hasActiveFilters || showExpiringOnly || showTestProjectsOnly;
    setIsFilterActive(isAnyFilterActive);

    // Fetch all projects if filter is active and we haven't fetched them yet
    if (isAnyFilterActive && allProjectsForFilter.length === 0) {
      fetchAllProjectsForFilter();
    }

    // Use all projects when filter/search is active, otherwise use paginated projects
    let sourceData = isAnyFilterActive ? allProjectsForFilter : projects;
    let filtered = sourceData;

    // Non-admins must never see test projects (covers the paginated source,
    // which is fetched separately from allProjectsForFilter)
    if (!isMainAdmin) {
      filtered = filtered.filter((project) => !isTestProjectFlag(project));
    }

    // Apply search term first (minimum 3 characters)
    if (searchTerm && searchTerm.trim().length >= 3) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          (project.customer &&
            project.customer
              .toString()
              .toLowerCase()
              .includes(lowercasedSearch)) ||
          (project.titleShort &&
            project.titleShort
              .toString()
              .toLowerCase()
              .includes(lowercasedSearch)) ||
          (project.projectDepartment &&
            project.projectDepartment
              .toString()
              .toLowerCase()
              .includes(lowercasedSearch))
      );
    }

    // Apply expiring filter if enabled
    if (showExpiringOnly) {
      const today = new Date();
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      filtered = filtered.filter(project => {
        if (!project.projectDueDate) return false;

        const dueDate = new Date(project.projectDueDate);
        return !isNaN(dueDate) &&
               dueDate <= oneMonthFromNow &&
               dueDate >= today;
      });
    }

    // Apply test projects filter if enabled (admin only)
    if (showTestProjectsOnly) {
      filtered = filtered.filter((project) => isTestProjectFlag(project));
    }

    // Then apply individual column filters
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        if (key === 'projectPhase') {
          // Select filters apply immediately
          filtered = filtered.filter(
            (project) => project[key] === filters[key]
          );
        } else if (
          key === 'projectDate' ||
          key === 'projectDueDate' ||
          key === 'ethicsCommitteeDate' ||
          key === 'firstInvoiceDate'
        ) {
          // Date filters apply immediately
          filtered = filtered.filter(
            (project) => project[key] && project[key].includes(filters[key])
          );
        } else {
          // Text filters require minimum 3 characters
          const filterValue = String(filters[key]);
          if (filterValue.trim().length >= 3) {
            filtered = filtered.filter((project) =>
              String(project[key] || '')
                .toLowerCase()
                .includes(filterValue.toLowerCase())
            );
          }
        }
      }
    });

    setFilteredProjects(filtered);
  }, [projects, allProjectsForFilter, filters, searchTerm, showExpiringOnly, showTestProjectsOnly, isMainAdmin]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Use stats data and calculate counts
  const activeCount = allProjectsForFilter.filter(
    (project) => {
      const phase = project.projectPhase?.toLowerCase();
      return phase === 'aktif' || phase === 'devam ediyor';
    }
  ).length || 0;
  const inactiveCount = allProjectsForFilter.filter(
    (project) => {
      const phase = project.projectPhase?.toLowerCase();
      return phase === 'pasif' || phase === 'tamamlandı';
    }
  ).length || 0;
  const totalPages = Math.ceil(totalProjects / pageSize);

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      fetchProjects(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      fetchProjects(currentPage - 1);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchProjects(page);
    }
  };

  const downloadExcel = async () => {
    try {
      // Fetch all projects for export
      toast.info('Excel dosyası hazırlanıyor...');
      const allProjectsQuery = query(collection(db, 'Projects'), orderBy('customer'));
      const allProjectsSnapshot = await getDocs(allProjectsQuery);
      let allProjects = allProjectsSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...Object.entries(data).reduce((acc, [key, value]) => {
            acc[key] = formatValue(value);
            return acc;
          }, {}),
        };
      });

      // Non-admins must never export test projects
      if (!isMainAdmin) {
        allProjects = allProjects.filter((project) => !isTestProjectFlag(project));
      }

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Projects');

      // Define columns based on visible columns
      const excelColumns = Object.keys(visibleColumns)
        .filter((key) => visibleColumns[key])
        .map((key) => ({
          header: columns[key].label,
          key,
          width: key === 'titleShort' || key === 'titleLong' ? 40 : 20,
        }));

      worksheet.columns = excelColumns;

      // Add data rows
      allProjects.forEach((project) => {
        const rowData = {};
        Object.keys(visibleColumns)
          .filter((key) => visibleColumns[key])
          .forEach((key) => {
            rowData[key] = project[key] || '';
          });
        worksheet.addRow(rowData);
      });

      // Style the header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' },
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // Generate the Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'projects.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Dosya indirirken hata oluştu');
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setScreenFailData(null);
  };

  const fetchScreenFailData = async (projectId) => {
    setLoadingScreenFails(true);
    try {
      // Fetch project doc fresh from Firestore for raw employee data
      const projectDoc = await getDoc(doc(db, 'Projects', projectId));
      const projectData = projectDoc.exists() ? projectDoc.data() : {};
      const projectEmployees = Array.isArray(projectData.employees) ? projectData.employees : [];

      // Build name map from project employees (name/surname set by WorkersModal)
      const empNameMap = {};
      projectEmployees.forEach(emp => {
        if (emp && emp.id) {
          empNameMap[emp.id] = `${emp.name || emp.eName || ''} ${emp.surname || emp.eSurname || ''}`.trim();
        }
      });

      const visitsQuery = query(
        collection(db, 'Visits'),
        where('projectId', '==', projectId),
        where('screenFail', '==', true)
      );
      const snapshot = await getDocs(visitsQuery);
      const screenFails = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      // For employees not found in project data, fall back to Employees collection
      const unresolvedEmpIds = [...new Set(
        screenFails
          .map(sf => sf.employeeId)
          .filter(id => id && !empNameMap[id])
      )];

      await Promise.all(unresolvedEmpIds.map(async (empId) => {
        try {
          const empDoc = await getDoc(doc(db, 'Employees', empId));
          if (empDoc.exists()) {
            const data = empDoc.data();
            empNameMap[empId] = `${data.eName || ''} ${data.eSurname || ''}`.trim();
          }
        } catch (e) {
          console.error('Error fetching employee:', empId, e);
        }
      }));

      const enriched = screenFails.map(sf => ({
        ...sf,
        employeeFullName: empNameMap[sf.employeeId] || sf.employeeName || sf.employeeId || '-'
      }));
      setScreenFailData(enriched);
    } catch (error) {
      console.error('Error fetching screen fail data:', error);
      toast.error('Screen fail verileri yüklenirken hata oluştu');
      setScreenFailData([]);
    } finally {
      setLoadingScreenFails(false);
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleStatusToggle = async () => {
    if (!selectedProject) return;

    const currentStatus = selectedProject.projectStatus || 'Aktif';
    const newStatus = currentStatus === 'Aktif' ? 'Pasif' : 'Aktif';

    // Show confirmation modal
    setConfirmMessage(`Proje Durumunu '${newStatus}' olarak değiştirmek istediğinizden emin misiniz?`);
    setPendingStatusChange(newStatus);
    setShowConfirmModal(true);
  };

  // ── "Projeyi Bitir" — flags a project as finished. The project stays active
  // (projectStatus is untouched); this only sets the isFinished flag. ──
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [finishConfirmMessage, setFinishConfirmMessage] = useState('');
  const [pendingFinishValue, setPendingFinishValue] = useState(null);

  const handleFinishToggle = () => {
    if (!selectedProject) return;
    const newValue = !selectedProject.isFinished;
    setPendingFinishValue(newValue);
    setFinishConfirmMessage(
      newValue
        ? `"${selectedProject.titleShort || 'Proje'}" projesini bitmiş olarak işaretlemek istediğinize emin misiniz? Proje aktif kalmaya devam edecek.`
        : `"${selectedProject.titleShort || 'Proje'}" projesinin "bitti" işaretini kaldırmak istediğinize emin misiniz?`
    );
    setShowFinishConfirm(true);
  };

  const handleConfirmFinish = async () => {
    if (!selectedProject || pendingFinishValue === null) return;
    try {
      const projectRef = doc(db, 'Projects', selectedProject.id);
      await updateDoc(projectRef, { isFinished: pendingFinishValue });

      const apply = (project) =>
        project.id === selectedProject.id ? { ...project, isFinished: pendingFinishValue } : project;

      setSelectedProject((prev) => ({ ...prev, isFinished: pendingFinishValue }));
      setProjects((prev) => prev.map(apply));
      setFilteredProjects((prev) => prev.map(apply));
      setAllProjectsForFilter((prev) => prev.map(apply));

      toast.success(pendingFinishValue ? 'Proje bitmiş olarak işaretlendi' : 'Projenin "bitti" işareti kaldırıldı');
    } catch (error) {
      console.error('Error updating project finished flag:', error);
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setShowFinishConfirm(false);
      setPendingFinishValue(null);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedProject || !pendingStatusChange) return;

    try {
      const projectRef = doc(db, 'Projects', selectedProject.id);
      await updateDoc(projectRef, { projectStatus: pendingStatusChange });

      // Update local state immediately
      const updatedProject = { ...selectedProject, projectStatus: pendingStatusChange };
      setSelectedProject(updatedProject);

      // Update the projects array
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === selectedProject.id
            ? { ...project, projectStatus: pendingStatusChange }
            : project
        )
      );

      // Update filtered projects
      setFilteredProjects((prevFiltered) =>
        prevFiltered.map((project) =>
          project.id === selectedProject.id
            ? { ...project, projectStatus: pendingStatusChange }
            : project
        )
      );

      // Update all projects for filter (for stats and filtering)
      setAllProjectsForFilter((prevAll) =>
        prevAll.map((project) =>
          project.id === selectedProject.id
            ? { ...project, projectStatus: pendingStatusChange }
            : project
        )
      );

      toast.success(`Proje durumu "${pendingStatusChange}" olarak güncellendi`);
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    } finally {
      setShowConfirmModal(false);
      setPendingStatusChange(null);
    }
  };

  const handleCancelStatusChange = () => {
    setShowConfirmModal(false);
    setPendingStatusChange(null);
    setConfirmMessage('');
  };

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();

    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft =
        tableContainerRef.current.scrollLeft +
        (direction === 'right' ? scrollAmount : -scrollAmount);

      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }

    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event

      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } =
          container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;

        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();

          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };

      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, {
        passive: false,
      });

      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [projects, filteredProjects]);

  // Admin only: Add test project function
  const handleAddTestProject = async () => {
    if (!isMainAdmin) return;
    
    try {
      toast.info('Test projesi oluşturuluyor...');
      
      const testProjectData = {
        customer: 'Test Müşteri',
        titleShort: `Test Projesi ${new Date().toLocaleDateString('tr-TR')}`,
        titleLong: 'Bu bir test projesidir - Admin tarafından oluşturuldu',
        protocolNumber: `TEST-${Date.now()}`,
        projectPhase: 'Aktif',
        projectDate: new Date().toISOString().split('T')[0],
        projectDueDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        projectDepartment: 'Test Departmanı',
        projectCentersType: 'Test',
        createdAt: serverTimestamp(),
        isTestProject: true
      };

      // Create the test project first
      const projectRef = await addDoc(collection(db, 'Projects'), testProjectData);
      const projectId = projectRef.id;

      // Define the specific users who should get tasks
      const targetEmails = [
        'melisa.sener@habcarecro.com',
        'aygul.dag@habcarecro.com',
        'ozge.yikimlar@habcarecro.com'
      ];

      // Fetch employee data for these specific emails
      const employeesQuery = query(
        collection(db, 'Employees'),
        where('eMail', 'in', targetEmails)
      );
      const employeesSnapshot = await getDocs(employeesQuery);
      
      // Create tasks for each target employee
      const taskPromises = employeesSnapshot.docs.map(async (empDoc) => {
        const empData = empDoc.data();
        const taskData = {
          title: `Yeni Test Projesi: ${testProjectData.titleShort}`,
          description: `${testProjectData.customer} müşterisi için yeni test projesi oluşturuldu. Proje detaylarını inceleyiniz ve gerekli işlemleri başlatınız.`,
          assigneeId: empDoc.id,
          assigneeName: `${empData.eName || ''} ${empData.eSurname || ''}`.trim(),
          assignees: [{
            id: empDoc.id,
            name: `${empData.eName || ''} ${empData.eSurname || ''}`.trim(),
            email: empData.eMail
          }],
          priority: 'low', // Test projesi için düşük öncelik
          status: 'pending',
          category: 'Test Projesi',
          projectId: projectId,
          projectName: testProjectData.titleShort,
          customerName: testProjectData.customer,
          createdAt: new Date(),
          updatedAt: new Date(),
          dueDate: new Date(testProjectData.projectDueDate),
          createdBy: 'System',
          requestType: 'TEST_PROJECT_TASK'
        };

        return addDoc(collection(db, 'Tasks'), taskData);
      });

      // Use batch write for tariffs to ensure atomicity
      const batch = writeBatch(db);

      // Create default FTE tariffs for the test project (0.0 to 1.0)
      for (let i = 0; i <= 10; i++) {
        const fteValue = (i / 10).toFixed(1);
        const tariffRef = doc(collection(db, 'Tariffs'));
        batch.set(tariffRef, {
          project: projectId,
          type: 'FTE',
          value: fteValue,
          price: 1000 * (i / 10),
          currency: 'TRY',
          createdAt: new Date(),
          isTestTariff: true
        });
      }

      // Add hourly tariffs
      const hourlyValues = ['50', '100', '150'];
      for (const value of hourlyValues) {
        const tariffRef = doc(collection(db, 'Tariffs'));
        batch.set(tariffRef, {
          project: projectId,
          type: 'Hourly',
          value: value,
          price: parseInt(value),
          currency: 'TRY',
          createdAt: new Date(),
          isTestTariff: true
        });
      }

      // Commit all tariffs and wait for tasks
      await Promise.all([
        batch.commit(),
        ...taskPromises
      ]);

      toast.success(`Test projesi ve tarifeleri başarıyla eklendi! ${employeesSnapshot.docs.length} kişi için görev oluşturuldu.`);
      refreshProjects();
    } catch (error) {
      console.error('Error adding test project:', error);
      toast.error('Test projesi eklenirken hata oluştu: ' + error.message);
    }
  };

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Proje Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button
                className={styles.addButton}
                onClick={() => setShowAddModal(true)}
              >
                <FaUserPlus className={styles.buttonIcon} /> Yeni Proje Ekle
              </button>
              {isMainAdmin && (
                <button 
                  className={styles.testButton} 
                  onClick={handleAddTestProject}
                >
                  <FaFlask className={styles.buttonIcon} /> Yeni Test Projesi Ekle
                </button>
              )}
              <button 
                className={styles.statsButton} 
                onClick={() => setShowStatsModal(true)}
              >
                <FaChartBar className={styles.buttonIcon} /> İstatistikler
              </button>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{totalProjects}</p>
                  <h3 className={styles.cardTitle}>Toplam Proje</h3>
                </div>
              </div>
            </div>

            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif</h3>
                </div>
              </div>
            </div>

            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>Pasif</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.expiringFilterContainer}>
            <div className={styles.expiringCheckbox}>
              <input
                type="checkbox"
                id="showExpiringOnly"
                checked={showExpiringOnly}
                onChange={(e) => setShowExpiringOnly(e.target.checked)}
              />
              <FaExclamationTriangle className={styles.warningIcon} />
              <label htmlFor="showExpiringOnly">
                Bitiş Tarihi Yaklaşanları Listele
              </label>
            </div>
            {isMainAdmin && (
              <div className={styles.testProjectCheckbox}>
                <input
                  type="checkbox"
                  id="showTestProjectsOnly"
                  checked={showTestProjectsOnly}
                  onChange={(e) => setShowTestProjectsOnly(e.target.checked)}
                />
                <FaFlask className={styles.testIcon} />
                <label htmlFor="showTestProjectsOnly">
                  Test Projelerini Göster
                </label>
              </div>
            )}
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Proje ara (min. 3 karakter)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <FaTimes />
                </button>
              )}
              {searchTerm && searchTerm.length > 0 && searchTerm.length < 3 && (
                <span className={styles.searchHint}>
                  En az 3 karakter girin
                </span>
              )}
            </div>

            <div className={styles.filterContainer} ref={filtersRef}>
              <button
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>

              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtrele ve Sütunları Seç</h3>
                    <button
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Filtreleri Temizle
                    </button>
                  </div>
                  

                  <div className={styles.columnVisibility}>
                    <h4>Görünür Sütunlar</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map((col) => (
                        <div className={styles.columnCheckbox} key={col}>
                          <input
                            type="checkbox"
                            id={`col-${col}`}
                            checked={visibleColumns[col] || false}
                            onChange={() => toggleColumnVisibility(col)}
                          />
                          <label htmlFor={`col-${col}`}>
                            {columns[col].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedProject && (
          <div
            className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}
          >
            <div className={styles.selectedProjectInfo}>
              <h3 className={styles.selectedName}>
                {selectedProject.titleShort || 'Seçili Proje'}
              </h3>
              <span
                className={`${styles.statusBadge} ${
                  ['aktif', 'devam ediyor'].includes(selectedProject.projectPhase?.toLowerCase())
                    ? styles.statusActive
                    : styles.statusInactive
                }`}
              >
                {selectedProject.projectPhase || 'Belirtilmemiş'}
              </span>
              {selectedProject.isFinished && (
                <span className={styles.finishedBadge}>
                  <FaFlagCheckered style={{ marginRight: 5 }} />
                  Bitti
                </span>
              )}
            </div>
            <div className={styles.actionButtons}>
              <button
                className={`${styles.actionButton} ${styles.editButton}`}
                onClick={handleEditClick}
              >
                <FaEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.statusButton}`}
                onClick={handleStatusToggle}
              >
                <FaExchangeAlt className={styles.actionIcon} />
                <span>Aktif/Pasif</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.finishButton}`}
                onClick={handleFinishToggle}
              >
                <FaFlagCheckered className={styles.actionIcon} />
                <span>{selectedProject.isFinished ? 'Bitişi Geri Al' : 'Projeyi Bitir'}</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.workersButton}`}
                onClick={() => setShowWorkersModal(true)}
              >
                <FaUserFriends className={styles.actionIcon} />
                <span>Çalışanlar</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.visitsButton}`}
                onClick={() => setShowVisitsModal(true)}
              >
                <FaCalendarAlt className={styles.actionIcon} />
                <span>Ziyaretler</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.patientsButton}`}
                onClick={() => setShowPatientsModal(true)}
              >
                <FaUserInjured className={styles.actionIcon} />
                <span>Hastalar</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.sponsorsButton}`}
              >
                <FaBuilding className={styles.actionIcon} />
                <span>Sponsorlar</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.filesButton}`}
                onClick={() => setShowFileUploadModal(true)}
              >
                <FaFolder className={styles.actionIcon} />
                <span>Dosyalar</span>
              </button>
              <button
                className={`${styles.actionButton} ${styles.screenFailButton}`}
                onClick={() => fetchScreenFailData(selectedProject.id)}
              >
                <FaExclamationTriangle className={styles.actionIcon} />
                <span>Screen Fail</span>
              </button>
            </div>
          </div>
        )}

        {/* Screen Fail Section */}
        {selectedProject && screenFailData !== null && (
          <div className={`${styles.screenFailSection} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.screenFailHeader}>
              <h3 className={styles.screenFailTitle}>
                <FaExclamationTriangle style={{ marginRight: '0.5rem', color: '#e53e3e' }} />
                Screen Fail Kayıtları — {selectedProject.titleShort || selectedProject.customer}
              </h3>
              <button
                className={styles.screenFailCloseButton}
                onClick={() => setScreenFailData(null)}
              >
                <FaTimes />
              </button>
            </div>
            {loadingScreenFails ? (
              <p className={styles.screenFailLoading}>Yükleniyor...</p>
            ) : screenFailData.length === 0 ? (
              <p className={styles.screenFailEmpty}>Bu projede screen fail kaydı bulunmamaktadır.</p>
            ) : (
              <>
                <div className={styles.screenFailSummary}>
                  <div className={styles.screenFailStat}>
                    <span className={styles.screenFailStatValue}>{screenFailData.length}</span>
                    <span className={styles.screenFailStatLabel}>Toplam Screen Fail</span>
                  </div>
                  <div className={styles.screenFailStat}>
                    <span className={styles.screenFailStatValue}>
                      {new Set(screenFailData.map(sf => sf.employeeId)).size}
                    </span>
                    <span className={styles.screenFailStatLabel}>Çalışan Sayısı</span>
                  </div>
                  <div className={styles.screenFailStat}>
                    <span className={styles.screenFailStatValue}>
                      {new Set(screenFailData.map(sf => sf.patientId).filter(p => p && p !== '-')).size}
                    </span>
                    <span className={styles.screenFailStatLabel}>Hasta Sayısı</span>
                  </div>
                </div>
                <div className={styles.screenFailTableWrapper}>
                  <table className={styles.screenFailTable}>
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Çalışan</th>
                        <th>Hasta ID</th>
                        <th>Vizit No</th>
                        <th>Bilgi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {screenFailData.map(sf => (
                        <tr key={sf.id}>
                          <td>{sf.firstVisitDate || sf.date || '-'}</td>
                          <td>{sf.employeeFullName}</td>
                          <td>{sf.patientId || '-'}</td>
                          <td>{sf.visitNumber || '-'}</td>
                          <td>{sf.info || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <section
          className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}
        >
          {/* Pagination Controls - Hidden when filters are active */}
          {!isFilterActive && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>
                  Sayfa {currentPage} / {totalPages} ({totalProjects} toplam proje)
                </span>
                {isLoading && <span className={styles.loadingIndicator}> Yükleniyor...</span>}
              </div>
              <div className={styles.paginationControls}>
                <button
                  className={styles.paginationButton}
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1 || isLoading}
                >
                  İlk
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1 || isLoading}
                >
                  <FaChevronLeft /> Önceki
                </button>
                <span className={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`${styles.pageNumber} ${currentPage === pageNum ? styles.activePage : ''}`}
                        onClick={() => goToPage(pageNum)}
                        disabled={isLoading}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </span>
                <button
                  className={styles.paginationButton}
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || isLoading}
                >
                  Sonraki <FaChevronRight />
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages || isLoading}
                >
                  Son
                </button>
              </div>
            </div>
          )}

          {/* Show filter info when filters are active */}
          {isFilterActive && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>
                  {filteredProjects.length} sonuç bulundu (toplam {totalProjects} proje içinden)
                </span>
              </div>
            </div>
          )}

          <div
            className={`${styles.tableWrapper} ${
              isDarkMode ? 'dark-mode' : ''
            }`}
          >
            <div
              className={`${styles.tableScrollContainer} ${
                isDarkMode ? 'dark-mode' : ''
              }`}
            >
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${
                  isDarkMode ? 'dark-mode' : ''
                }`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table
                  className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}
                >
                  <thead>
                    <tr>
                      {Object.keys(columns).map(
                        (colKey) =>
                          visibleColumns[colKey] && (
                            <th
                              key={colKey}
                              className={
                                colKey === 'projectPhase'
                                  ? styles.statusColumn
                                  : ''
                              }
                            >
                              <div className={styles.columnHeader}>
                                <span>{columns[colKey].label}</span>
                                <TableFilter
                                  column={colKey}
                                  type={columns[colKey].type}
                                  options={columns[colKey].options}
                                  onFilter={handleFilter}
                                  currentValue={filters[colKey] || ''}
                                />
                              </div>
                            </th>
                          )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.length > 0 ? (
                      filteredProjects.map(
                        (project) =>
                          !project.isEmpty && (
                            <tr
                              key={project.id}
                              onClick={() => handleProjectSelect(project)}
                              className={
                                selectedProject &&
                                selectedProject.id === project.id
                                  ? styles.selectedRow
                                  : ''
                              }
                            >
                              {Object.keys(columns).map(
                                (colKey) =>
                                  visibleColumns[colKey] && (
                                    <td
                                      key={colKey}
                                      title={project[colKey]}
                                      className={
                                        colKey === 'projectStatus'
                                          ? styles.statusColumn
                                          : ''
                                      }
                                    >
                                      <div className={styles.tdContentWrapper}>
                                        {colKey === 'projectStatus' ? (
                                          <span
                                            className={`${styles.statusBadge} ${
                                              project[colKey] === undefined ||
                                              project[colKey] === null ||
                                              project[colKey] === ''
                                                ? styles.statusNoData
                                                : project[colKey] === 'Aktif'
                                                ? styles.statusActive
                                                : styles.statusInactive
                                            }`}
                                          >
                                            {project[colKey] === undefined ||
                                            project[colKey] === null ||
                                            project[colKey] === ''
                                              ? '-'
                                              : project[colKey]}
                                          </span>
                                        ) : colKey === 'projectPhase' ? (
                                          <span
                                            className={`${styles.statusBadge} ${
                                              project[colKey] === undefined ||
                                              project[colKey] === null
                                                ? styles.statusNoData
                                                : (() => {
                                                    const phaseValue = project[colKey]?.toLowerCase();
                                                    const isActive = ['aktif', 'devam ediyor'].includes(phaseValue);
                                                    return isActive ? styles.statusActive : styles.statusInactive;
                                                  })()
                                            }`}
                                          >
                                            {project[colKey] === undefined ||
                                            project[colKey] === null
                                              ? '-'
                                              : truncateText(project[colKey])}
                                          </span>
                                        ) : (
                                          <div className={styles.cellContent}>
                                            {truncateText(
                                              project[colKey],
                                              colKey === 'titleShort' ||
                                                colKey === 'titleLong'
                                                ? 40
                                                : 15
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  )
                              )}
                            </tr>
                          )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            Object.keys(visibleColumns).filter(
                              (key) => visibleColumns[key]
                            ).length
                          }
                          className={styles.noDataCell}
                        >
                          <div className={styles.noData}>
                            <FaFile className={styles.noDataIcon} />
                            <p>Filtrelere uygun proje bulunamadı</p>
                            {(Object.keys(filters).length > 0 ||
                              searchTerm) && (
                              <button
                                className={styles.clearFiltersButton}
                                onClick={clearFilters}
                              >
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {showVisitsModal && selectedProject && (
          <VisitsModal
            project={selectedProject}
            onClose={() => setShowVisitsModal(false)}
          />
        )}

        {showWorkersModal && selectedProject && (
          <WorkersModal
            project={selectedProject}
            onClose={() => setShowWorkersModal(false)}
          />
        )}

        {showAddModal && (
          <AddProjectModal
            onClose={() => setShowAddModal(false)}
            onUpdate={refreshProjects}
          />
        )}

        {showEditModal && selectedProject && (
          <EditProjectModal
            project={selectedProject}
            onClose={() => setShowEditModal(false)}
            onUpdate={refreshProjects}
          />
        )}

        {showFileUploadModal && selectedProject && (
          <FileUploadModal
            project={selectedProject}
            onClose={() => {
              setShowFileUploadModal(false);
              refreshProjects();
            }}
          />
        )}

        {showPatientsModal && selectedProject && (
          <PatientsModal
            project={selectedProject}
            onClose={() => setShowPatientsModal(false)}
          />
        )}

        {showConfirmModal && (
          <ConfirmationModal
            message={confirmMessage}
            onConfirm={handleConfirmStatusChange}
            onCancel={handleCancelStatusChange}
          />
        )}

        {showFinishConfirm && (
          <ConfirmationModal
            message={finishConfirmMessage}
            onConfirm={handleConfirmFinish}
            onCancel={() => {
              setShowFinishConfirm(false);
              setPendingFinishValue(null);
            }}
          />
        )}

        {showStatsModal && (
          <ProjectStatsModal
            projects={allProjectsForFilter}
            onClose={() => setShowStatsModal(false)}
          />
        )}

        {showVideoModal && (
          <div className={styles.videoModal}>
            <div className={styles.videoContent}>
              <video controls>
                <source src={currentVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <button
                className={styles.closeVideoButton}
                onClick={() => setShowVideoModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    </div>
  );
};

export default ProjectsManagementPage;
